import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';
import { 
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend
} from 'recharts';
import { 
  FaCalendarDay, 
  FaCalendarWeek, 
  FaStar, 
  FaTruck, 
  FaClock,
  FaCheckCircle,
  FaSpinner,
  FaChartLine
} from 'react-icons/fa';
import './AdminStats.css';

const AdminStats = () => {
  const [stats, setStats] = useState({
    totalRendezVous: 0,
    aujourdhui: 0,
    ceMois: 0,
    avisTotal: 0,
    moyenneAvis: 0
  });
  const [parStatut, setParStatut] = useState({
    'en attente': 0,
    'confirmé': 0,
    'terminé': 0
  });
  const [evolutionMois, setEvolutionMois] = useState([]);
  const [loading, setLoading] = useState(true);

  const COLORS = {
    'en attente': '#f59e0b',
    'confirmé': '#10b981',
    'terminé': '#3b82f6'
  };

  useEffect(() => {
    setLoading(true);
    
    
    const rendezvousRef = ref(database, 'rendezvous');
    const unsubscribeRdv = onValue(rendezvousRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let total = 0;
        let aujourdhui = 0;
        let ceMois = 0;
        const statuts = { 'en attente': 0, 'confirmé': 0, 'terminé': 0 };
        
       
        const moisMap = new Map();
        const aujourdhuiDate = new Date().toISOString().split('T')[0];
        const moisActuel = new Date().getMonth();
        const anneeActuel = new Date().getFullYear();
        
        Object.values(data).forEach(userRdv => {
          Object.values(userRdv).forEach(rdv => {
            total++;
            
            if (statuts.hasOwnProperty(rdv.statut)) {
              statuts[rdv.statut]++;
            }
            
            if (rdv.date === aujourdhuiDate) {
              aujourdhui++;
            }
            
            const rdvDate = new Date(rdv.date);
            if (rdvDate.getMonth() === moisActuel && rdvDate.getFullYear() === anneeActuel) {
              ceMois++;
            }
            
           
            const moisKey = `${rdvDate.getMonth() + 1}/${rdvDate.getFullYear()}`;
            const moisNom = rdvDate.toLocaleString('fr-FR', { month: 'short' });
            if (!moisMap.has(moisKey)) {
              moisMap.set(moisKey, { mois: moisNom, total: 0, enAttente: 0, confirmé: 0, terminé: 0 });
            }
            const moisData = moisMap.get(moisKey);
            moisData.total++;
            if (rdv.statut === 'en attente') moisData.enAttente++;
            if (rdv.statut === 'confirmé') moisData.confirmé++;
            if (rdv.statut === 'terminé') moisData.terminé++;
          });
        });
        
        setStats(prev => ({
          ...prev,
          totalRendezVous: total,
          aujourdhui,
          ceMois
        }));
        setParStatut(statuts);
        
        
        const evolutionArray = Array.from(moisMap.values()).slice(-6);
        setEvolutionMois(evolutionArray);
      }
      setLoading(false);
    });

    
    const avisRef = ref(database, 'avis');
    const unsubscribeAvis = onValue(avisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const avisList = Object.values(data);
        const totalAvis = avisList.length;
        const moyenne = avisList.reduce((acc, avis) => acc + (avis.note || 0), 0) / totalAvis || 0;
        
        setStats(prev => ({
          ...prev,
          avisTotal: totalAvis,
          moyenneAvis: moyenne.toFixed(1)
        }));
      }
    });

    return () => {
      unsubscribeRdv();
      unsubscribeAvis();
    };
  }, []);

  const statCards = [
    { title: 'Rendez-vous total', value: stats.totalRendezVous, icon: <FaCalendarWeek />, color: '#3b82f6', bgColor: '#eff6ff' },
    { title: "Aujourd'hui", value: stats.aujourdhui, icon: <FaCalendarDay />, color: '#10b981', bgColor: '#ecfdf5' },
    { title: 'Ce mois', value: stats.ceMois, icon: <FaChartLine />, color: '#f59e0b', bgColor: '#fffbeb' },
    { title: 'Avis clients', value: stats.avisTotal, icon: <FaStar />, color: '#8b5cf6', bgColor: '#f5f3ff' }
  ];

  const statutData = Object.entries(parStatut).map(([name, value]) => ({
    name: name === 'en attente' ? 'En attente' : name === 'confirmé' ? 'Confirmé' : 'Terminé',
    value,
    color: COLORS[name]
  }));

  if (loading) {
    return (
      <div className="admin-stats-loading">
        <div className="spinner"></div>
        <p>Chargement des statistiques...</p>
      </div>
    );
  }

  return (
    <div className="admin-stats-modern">
      <div className="stats-header">
        <div>
          <h2>Tableau de bord</h2>
          <p>Bienvenue dans votre espace d'administration</p>
        </div>
        <div className="stats-date">
          <FaCalendarDay />
          <span>{new Date().toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      </div>

      
      <div className="stats-cards-grid">
        {statCards.map((card, index) => (
          <div key={index} className="stats-card" style={{ borderTopColor: card.color }}>
            <div className="stats-card-icon" style={{ background: card.bgColor, color: card.color }}>
              {card.icon}
            </div>
            <div className="stats-card-info">
              <h3>{card.value}</h3>
              <p>{card.title}</p>
            </div>
          </div>
        ))}
      </div>

      
      <div className="stats-charts">
        
        <div className="chart-card">
          <div className="chart-header">
            <h3>Répartition des rendez-vous</h3>
            <p>État actuel de tous les rendez-vous</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statutData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={entry => `${entry.name}: ${entry.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statutData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-legend">
            {statutData.map((item, index) => (
              <div key={index} className="legend-item">
                <div className="legend-color" style={{ background: item.color }}></div>
                <span>{item.name}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </div>

        
        {evolutionMois.length > 0 && (
          <div className="chart-card">
            <div className="chart-header">
              <h3>Évolution des rendez-vous</h3>
              <p>Nombre de rendez-vous par mois</p>
            </div>
            <div className="chart-content">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={evolutionMois}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mois" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="confirmé" name="Confirmés" fill="#10b981" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="terminé" name="Terminés" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        
        {stats.moyenneAvis > 0 && (
          <div className="chart-card rating-card">
            <div className="rating-content">
              <div className="rating-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStar 
                    key={i} 
                    className={i < Math.floor(stats.moyenneAvis) ? 'star-filled' : 'star-empty'} 
                  />
                ))}
              </div>
              <div className="rating-value">
                <span className="rating-number">{stats.moyenneAvis}</span>
                <span className="rating-max">/5</span>
              </div>
              <p>Note moyenne des avis clients</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStats;